import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { BackButton, Button } from '../../components/common';
import { Card } from '../../components/common/Card';
import { Heading, Text, Typography } from '../../components/common/Typography';
import StatusBadge from '../../components/common/StatusBadge';
import ClubLogo from '../../components/clubs/ClubLogo';
import { fetchPlayerRecentSessions } from '../../services/playerService';
import { normalizePlayerRecentSession } from '../../components/player/PlayerRecentSessionsCarousel';

/**
 * Full list of the player's recent sessions — linked from carousel "View all" CTAs.
 */
const ProfileMySessions = () => {
  const { currentUser } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clubIdFilter = searchParams.get('clubId');

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser?.id) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPlayerRecentSessions(currentUser.id, 50)
      .then((rows) => {
        if (cancelled) return;
        setSessions((rows || []).map(normalizePlayerRecentSession));
      })
      .catch((err) => {
        console.error('Error loading sessions list:', err);
        if (!cancelled) setError('Failed to load sessions. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  const filteredSessions = useMemo(() => {
    if (!clubIdFilter) return sessions;
    return sessions.filter((s) => s.clubId === clubIdFilter);
  }, [sessions, clubIdFilter]);

  const clubLabel = clubIdFilter
    ? filteredSessions[0]?.clubName || 'this club'
    : null;

  const openSession = (session) => {
    if (!session?.id || !session?.clubId || !currentUser?.id) return;
    navigate(`/player/${currentUser.id}/session/${session.id}`, {
      state: { clubId: session.clubId },
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <BackButton onClick={() => navigate(-1)}>Back</BackButton>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <BackButton onClick={() => navigate(-1)}>Back</BackButton>
        <Card variant="default" size="large">
          <div className="text-center py-8">
            <Text className="text-error mb-4">{error}</Text>
            <Button type="button" variant="primary" size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton onClick={() => navigate(-1)}>Back</BackButton>

      <div>
        <Heading level={2} className="mb-2">
          My Sessions
        </Heading>
        <Text className="text-text-secondary">
          {clubLabel
            ? `Sessions you've played at ${clubLabel}.`
            : "All sessions you've played across your clubs."}
        </Text>
      </div>

      {filteredSessions.length === 0 ? (
        <Card variant="default" size="large">
          <div className="text-center py-12 px-4">
            <div className="text-5xl mb-4" aria-hidden>🏸</div>
            <Heading level={3} className="mb-2">No sessions yet</Heading>
            <Text className="text-text-secondary">
              Your session history will appear here after you play.
            </Text>
          </div>
        </Card>
      ) : (
        <ul className="space-y-3">
          {filteredSessions.map((session) => {
            const dateStr = session.date
              ? format(
                  session.date instanceof Date ? session.date : parseISO(String(session.date)),
                  'EEE, d MMM yyyy'
                )
              : '—';
            const winRatePct = session.winRatePct;
            const eloDelta = (session.totalEloDelta ?? 0) || (session.mixedEloDelta ?? 0) + (session.levelsEloDelta ?? 0);

            return (
              <li key={`${session.clubId}-${session.id}`}>
                <button
                  type="button"
                  onClick={() => openSession(session)}
                  className="w-full text-left rounded-2xl border border-border-medium bg-surface px-4 py-4 shadow-brand-sm transition-colors hover:bg-surface-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <Typography className="font-semibold text-text-primary truncate">
                        {session.name}
                      </Typography>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-muted">
                        {session.clubName ? (
                          <span className="inline-flex items-center gap-1.5 min-w-0">
                            {session.clubLogoImageUrl ? (
                              <ClubLogo
                                logoImageUrl={session.clubLogoImageUrl}
                                clubName={session.clubName}
                                size="sm"
                                className="shrink-0"
                              />
                            ) : null}
                            <span className="truncate">{session.clubName}</span>
                          </span>
                        ) : null}
                        {session.clubName ? <span aria-hidden>·</span> : null}
                        <span>{dateStr}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Typography variant="caption" className="font-semibold text-text-primary">
                          {session.wins}W / {session.losses}L
                        </Typography>
                        {winRatePct != null ? (
                          <StatusBadge variant={winRatePct >= 50 ? 'success' : 'default'} size="small">
                            {winRatePct}%
                          </StatusBadge>
                        ) : null}
                        <Typography variant="caption" className="text-text-muted">
                          ELO{' '}
                          <span className={eloDelta > 0 ? 'text-success font-semibold' : eloDelta < 0 ? 'text-danger font-semibold' : ''}>
                            {eloDelta > 0 ? '+' : ''}{eloDelta}
                          </span>
                        </Typography>
                      </div>
                    </div>
                    <svg className="w-4 h-4 shrink-0 text-text-muted mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ProfileMySessions;
